import {
  buildTestIds,
  useAuth,
  useCategories,
  useEnvs,
  userAuthenticatedActions,
  useShallow,
} from "application";
import { useRouter } from "next/router";
import {
  Dispatch,
  Fragment,
  MouseEventHandler,
  SetStateAction,
  useCallback,
} from "react";
import { CloseIcon, theme, Title } from "ui";
import { cookie } from "implementations";
import { AllConfigs, CategoryDto, MenuTypes } from "typing";
import {
  AuthenticatedOptionDefault,
  AuthenticatedOptionWithLink,
  CategoryExploreIcon,
  CategorySidebarMenu,
  LinkWrapper,
  ListCategoriesSidebarMenu,
  CategoriesList,
  TitleAction,
  TitleWrapper,
  UserAuthenticatedActionsSidebarMenu,
  WrapperIcon,
} from "../../styles";
import { Link } from "../../../../../../../Link";
import { OffersList } from "../OffersList";
import { Categories, HighLightCategories } from "../../data";
import { ServicesList } from "../ServicesList";

interface FirstLevelCategoriesProps {
  onCloseSidebarMenu: () => void;
  setCategoriesLevel: Dispatch<SetStateAction<MenuTypes>>;
}

type HighlightCategoryOption = {
  name: string;
  descriptionUrl: string;
  excludeDivider?: boolean;
  color?: string;
};

const iconColor = `${theme.colors.neutral["999"]}`;

const FirstLevelCategories = ({
  onCloseSidebarMenu,
  setCategoriesLevel,
}: FirstLevelCategoriesProps) => {
  const { isAuthenticated, signOutAccount } = useAuth();
  const allConfigs = useEnvs((state) => state.allConfigs);
  const router = useRouter();
  const { setSelectedCategory, setCategoryTitle, setCategoryUrl } =
    useCategories(
      useShallow((state) => ({
        setSelectedCategory: state.setSelectedCategory,
        setCategoryTitle: state.setCategoryTitle,
        setCategoryUrl: state.setCategoryUrl,
      }))
    );

  const categories = Categories();

  const highLightCategories = HighLightCategories()?.filter(
    (category) =>
      category?.descriptionUrl?.toLowerCase()?.includes("construcao") ||
      category?.descriptionUrl?.toLowerCase()?.includes("iluminacao-categoria")
  );

  const handleCategoryClick = (
    title: string,
    url: string,
    category?: CategoryDto[]
  ) => {
    if (category) {
      setSelectedCategory(category);
      setCategoriesLevel("SecondLevel");
      setCategoryTitle(title);
      setCategoryUrl(url);
    }
  };

  const handleCategoriesNavigation = useCallback(
    (category: HighlightCategoryOption) => {
      router.push(`/${category?.descriptionUrl}`);
      onCloseSidebarMenu();
    },
    [onCloseSidebarMenu, router]
  );

  const handleSignOut = useCallback(() => {
    signOutAccount(cookie);
    onCloseSidebarMenu();
  }, [onCloseSidebarMenu, signOutAccount]);

  return (
    <ListCategoriesSidebarMenu
      {...buildTestIds("list-categories-sidebar-menu")}
    >
      {userAuthenticatedActions(allConfigs as AllConfigs)?.map((action) => (
        <AuthenticatedOptionDefault
          {...buildTestIds(`action-item-link-${action.title}`)}
          key={action?.id}
          as={AuthenticatedOptionWithLink}
          href={action.path}
          target="_self"
          onClick={() =>
            onCloseSidebarMenu() as unknown as MouseEventHandler<HTMLAnchorElement>
          }
        >
          <LinkWrapper {...buildTestIds(`action-link-wrapper-${action.title}`)}>
            <UserAuthenticatedActionsSidebarMenu
              {...buildTestIds(`action-item-wrapper-${action.title}`)}
            >
              <WrapperIcon
                {...buildTestIds(`action-item-wrapper-icon-${action.title}`)}
              >
                {action.icon(iconColor)}
              </WrapperIcon>
              <TitleAction
                {...buildTestIds(`action-item-title-${action.title}`)}
              >
                {action.title}
              </TitleAction>
            </UserAuthenticatedActionsSidebarMenu>
          </LinkWrapper>
        </AuthenticatedOptionDefault>
      ))}

      {isAuthenticated && (
        <AuthenticatedOptionDefault
          {...buildTestIds(`action-item-link-Sair`)}
          as={AuthenticatedOptionDefault}
          href="/"
          target="_self"
          onClick={handleSignOut}
        >
          <LinkWrapper {...buildTestIds(`action-link-wrapper-Sair`)}>
            <UserAuthenticatedActionsSidebarMenu
              {...buildTestIds(`action-item-wrapper-Sair`)}
            >
              <WrapperIcon {...buildTestIds(`action-item-wrapper-icon-Sair`)}>
                <CloseIcon
                  width={20}
                  height={20}
                  color={theme.colors.neutral["999"]}
                />
              </WrapperIcon>
              <TitleAction {...buildTestIds(`action-item-title-Sair`)}>
                Sair
              </TitleAction>
            </UserAuthenticatedActionsSidebarMenu>
          </LinkWrapper>
        </AuthenticatedOptionDefault>
      )}

      <TitleWrapper
        {...buildTestIds("title-wrapper")}
        isAuthenticated={isAuthenticated}
      >
        <Title
          {...buildTestIds("title-categories-sidebar-menu-mobile")}
          color={theme.colors.neutral["999"]}
          fontSize={theme.typography.fontSize.sm.x5}
          fontWeight={theme.typography.fontWeight["500"]}
        >
          Categorias
        </Title>
      </TitleWrapper>
      {categories?.map((category) => (
        <Fragment
          {...buildTestIds(`categories-fragment-${category?.title}`)}
          key={category?.id}
        >
          <CategoriesList
            {...buildTestIds(`categories-list-${category?.title}`)}
            onClick={() =>
              handleCategoryClick(
                category?.title,
                category?.descriptionUrl,
                category?.categories
              )
            }
          >
            <CategorySidebarMenu
              {...buildTestIds(`category-title-${category?.title}`)}
            >
              {category?.image}
              {category?.title}
            </CategorySidebarMenu>
            <CategoryExploreIcon />
          </CategoriesList>
        </Fragment>
      ))}

      {highLightCategories?.map((category) => (
        <Fragment
          {...buildTestIds(`categories-fragment-${category?.name}`)}
          key={category?.name}
        >
          <CategoriesList onClick={() => handleCategoriesNavigation(category)}>
            <Link
              {...buildTestIds(`categories-link-${category?.name}`)}
              key={category?.name}
              href={`/${category?.descriptionUrl}`}
              target="_self"
            >
              <CategorySidebarMenu
                {...buildTestIds(`category-name-${category?.name}`)}
              >
                {category?.image}
                {category?.name}
              </CategorySidebarMenu>
              <CategoryExploreIcon />
            </Link>
          </CategoriesList>
        </Fragment>
      ))}

      <OffersList
        {...buildTestIds("offer-list")}
        isAuthenticated={!!isAuthenticated}
        onCloseSidebarMenu={onCloseSidebarMenu}
        setCategoriesLevel={setCategoriesLevel}
      />
      <ServicesList
        onCloseSidebarMenu={onCloseSidebarMenu}
        setCategoriesLevel={setCategoriesLevel}
      />
    </ListCategoriesSidebarMenu>
  );
};

export { FirstLevelCategories };
